import { Button, Popover, styled } from '@mui/material';
import { format } from 'date-fns';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useCallback, useState } from 'react';

import Arrow from '../../../../assets/icons/svg/arrowDropdown.svg';
import Calendar from '../../../../assets/icons/svg/calendar.svg';
import type { ITimeframe } from '../../../../core/types/dateTypes';
import { DateRange } from './components/DateRange';
import { TimeInterval } from './components/TimeInterval';

interface IProps {
  range: {
    startDate: Date;
    endDate: Date;
    timeframe: ITimeframe;
  };
  onChange: (props: { startDate: Date; endDate: Date; timeframe: ITimeframe }) => void;
  displayTimeFrameInterval: boolean;
}

export const DateRangeSelect = ({ range, onChange, displayTimeFrameInterval }: IProps) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'dateRange',
  });

  const [calDate, setCalDate] = useState<{
    startDate: Date;
    endDate: Date;
    timeframe: ITimeframe;
  }>({ ...range });

  const onApply = useCallback(() => {
    onChange(calDate);
    popupState.close();
  }, [calDate, onChange, popupState]);

  const onClear = useCallback(() => {
    setCalDate({ ...range });
  }, [range]);

  return (
    <>
      <Wrapper {...bindTrigger(popupState)}>
        <CalendarIcon />
        <Text>
          {format(range.startDate, 'MMM dd yyyy')} - {format(range.endDate, 'MMM dd yyyy')}
        </Text>
        <ArrowIcon />
      </Wrapper>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <PopoverWrapper>
          <Header>
            <Button size={'small'} variant={'contained'} onClick={onClear} color="inherit">
              Clear Filter
            </Button>
            <Button size={'small'} variant={'contained'} onClick={onApply} color="primary">
              Apply Time Range
            </Button>
          </Header>
          <DateRange
            range={calDate}
            onChange={(r) => setCalDate({ timeframe: calDate.timeframe, ...r })}
          />
          {displayTimeFrameInterval && (
            <TimeInterval
              timeframe={calDate.timeframe}
              onChange={(r) => setCalDate({ ...calDate, timeframe: r })}
            />
          )}
        </PopoverWrapper>
      </Popover>
    </>
  );
};

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  padding: '8px',
  width: '288px',
  borderRadius: '8px',
  border: '1px solid #B8C3C8',
  fontSize: '16px',
  fontFamily: theme.fonts.telka,
  cursor: 'pointer',
  height: '40px',
  textBoxTrim: 'trim-both',
}));

const Text = styled('div')`
  flex: 1;
  margin-top: 2px;
  white-space: nowrap;
`;

const ArrowIcon = styled('div')`
  width: 20px;
  height: 20px;
  mask: url(${Arrow}) no-repeat center / contain;
  transition: transform 0.3s;
  background-color: #65665e;
`;

const CalendarIcon = styled('div')`
  width: 20px;
  height: 20px;
  mask: url(${Calendar}) no-repeat center / contain;
  transition: transform 0.3s;
  background-color: #65665e;
`;

const PopoverWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin: 16px 0 16px 8px;
`;

const Header = styled('div')`
  display: flex;
  gap: 8px;
  justify-content: end;
  margin-right: 8px;
`;
